<template>
  <div class="settings-org-section">
    <SettingsSectionHeader
      title="Change Log"
      subtitle="Global audit feed (HR profile, compensation, time-off, recycle, and login — Settings CRUD is not audited yet)"
      icon="pi-history"
    >
      <template #actions>
        <Button
          label="Export CSV"
          icon="pi pi-download"
          size="small"
          severity="secondary"
          outlined
          :disabled="!entries.length || exporting"
          :loading="exporting"
          @click="exportCsv"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadEntries"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-org-section__body settings-changelog-body">
      <div class="settings-changelog-toolbar">
        <SettingsChangelogFiltersBar
          v-model:search="filters.search"
          v-model:action="filters.action"
          v-model:entity-type="filters.entity_type"
          v-model:date-from="filters.date_from"
          v-model:date-to="filters.date_to"
          v-model:employee-hr-id="filters.employee_hr_id"
          v-model:employee-label="filters.employee_label"
          v-model:employee-snapshot="filters.employee_snapshot"
          :can-view-compensation="canViewCompensation"
          @apply="applyFilters"
          @clear="clearFilters"
        />

        <p v-if="!loading && total > 0" class="settings-changelog-results">
          {{ total }} {{ total === 1 ? 'entry' : 'entries' }}
          <span v-if="filtersActive"> matching filters</span>
        </p>
      </div>

      <div class="settings-changelog-table-wrap">
        <div v-if="loading && !entries.length" class="settings-org-section__state">
          <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
        </div>

        <Message v-else-if="error" severity="error" :closable="false" class="settings-org-section__message">
          <div class="flex flex-col sm:flex-row sm:items-center gap-3">
            <span class="flex-1">{{ error }}</span>
            <Button label="Retry" size="small" severity="danger" outlined @click="loadEntries" />
          </div>
        </Message>

        <div
          v-else-if="!entries.length"
          class="settings-org-section__empty"
        >
          <i class="pi pi-history settings-org-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="filtersActive"
            label="Clear filters"
            size="small"
            severity="secondary"
            outlined
            class="mt-2"
            @click="clearFilters"
          />
        </div>

        <template v-else>
          <DataTable
          :value="entries"
          size="small"
          class="settings-changelog-table"
          dataKey="id"
          sort-mode="single"
          removable-sort
          :sort-field="sortField"
          :sort-order="sortOrder"
          @sort="onSort"
        >
          <Column field="created_at" header="When" sortable style="width: 9.5rem">
            <template #body="{ data }">
              <span class="text-xs whitespace-nowrap">{{ formatWhen(data.created_at) }}</span>
            </template>
          </Column>
          <Column field="actor_name" header="Actor" sortable style="width: 10rem">
            <template #body="{ data }">
              {{ data.actor_name || 'System' }}
            </template>
          </Column>
          <Column field="target_label" header="Employee" sortable style="width: 14rem">
            <template #body="{ data }">
              <button
                v-if="targetEmployeePerson(data)"
                type="button"
                class="settings-changelog-employee-btn"
                :title="`Filter by ${data.target_label}`"
                @click="filterByEmployee(data)"
              >
                <EmployeeReportPersonRow
                  compact
                  :person="targetEmployeePerson(data)"
                  :show-department-tag="true"
                  :link-to-profile="false"
                />
              </button>
              <span v-else>{{ data.target_label || '—' }}</span>
            </template>
          </Column>
          <Column field="action" header="Action" sortable style="width: 7.5rem">
            <template #body="{ data }">
              <Tag
                :value="auditActionLabel(data.action)"
                :severity="changelogActionSeverity(data.action)"
                class="text-xs"
              />
            </template>
          </Column>
          <Column field="entity_type" header="Source" style="width: 8.5rem">
            <template #body="{ data }">
              <span class="settings-changelog-source">{{ changelogEntityTypeLabel(data.entity_type) }}</span>
            </template>
          </Column>
          <Column field="field_name" header="Field">
            <template #body="{ data }">
              {{ auditFieldLabel(data) }}
            </template>
          </Column>
          <Column header="Change">
            <template #body="{ data }">
              <EmployeeHighlightAuditChange
                v-if="data.field_name === 'highlight_color'"
                :entry="data"
              />
              <div v-else class="text-xs min-w-0 settings-changelog-change">
                <template v-if="showAuditChangeAsSingle(data)">
                  <span class="text-surface-800 dark:text-surface-200 break-all">
                    {{ singleAuditChangeText(data) }}
                  </span>
                </template>
                <template v-else>
                  <span class="text-surface-500 line-through break-all">{{ formatAuditChangeOld(data) }}</span>
                  <i class="pi pi-arrow-right text-[0.6rem] mx-1.5 text-surface-400" />
                  <span class="text-surface-800 dark:text-surface-200 break-all">{{ formatAuditChangeNew(data) }}</span>
                </template>
              </div>
            </template>
          </Column>
          </DataTable>

          <div v-if="total > entries.length" class="flex justify-center mt-3 pb-2">
            <Button
              label="Load more"
              icon="pi pi-chevron-down"
              size="small"
              severity="secondary"
              outlined
              :loading="loadingMore"
              @click="loadMore"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import Tag from 'primevue/tag';
import { useAuth } from '@/composables/useAuth';
import { fetchGlobalAudit } from '@/api/auditApi';
import { isValidDateRange } from '@/constants/attendance';
import { downloadCsv } from '@/utils/fileDownload';
import { formatDateTimeShort } from '@/utils/common';
import {
  auditActionLabel,
  auditFieldLabel,
  displayAuditValue,
  formatAuditChangeNew,
  formatAuditChangeOld,
  showAuditChangeAsSingle,
  singleAuditChangeText,
} from '@/utils/auditDisplayUtils';
import SettingsChangelogFiltersBar from '../SettingsChangelogFiltersBar.vue';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import EmployeeReportPersonRow from '@/components/employeeReports/EmployeeReportPersonRow.vue';
import EmployeeHighlightAuditChange from '@/components/employees/EmployeeHighlightAuditChange.vue';
import {
  changelogActionSeverity,
  changelogEntityTypeLabel,
  changelogTargetEmployeePerson,
  hasActiveChangelogFilters,
} from '../../constants/changelogFilters';
import { useClientTableSort } from '@/utils/tableSort';

const toast = useToast();
const { isSuperAdmin, hasPermission } = useAuth();
const pageSize = 50;
const { sortField, sortOrder, onSort } = useClientTableSort('created_at', -1);

const entries = ref([]);
const total = ref(0);
const loading = ref(false);
const loadingMore = ref(false);
const exporting = ref(false);
const error = ref(null);

const canViewCompensation = computed(
  () => isSuperAdmin.value || hasPermission('compensation.view_all')
);

const filters = reactive({
  search: '',
  action: null,
  entity_type: null,
  date_from: '',
  date_to: '',
  employee_hr_id: null,
  employee_label: '',
  employee_snapshot: null,
});

const activeFilters = reactive({
  search: '',
  action: null,
  entity_type: null,
  date_from: '',
  date_to: '',
  employee_hr_id: null,
  employee_label: '',
});

const filtersActive = computed(() => hasActiveChangelogFilters(activeFilters));

const emptyMessage = computed(() =>
  filtersActive.value
    ? 'No entries match the current filters.'
    : 'No change log entries yet.'
);

function formatWhen(value) {
  const formatted = formatDateTimeShort(value);
  return formatted && formatted !== '-' ? formatted : (value || '—');
}

function syncActiveFilters() {
  activeFilters.search = filters.search.trim();
  activeFilters.action = filters.action || null;
  activeFilters.entity_type = filters.entity_type || null;
  activeFilters.date_from = filters.date_from || '';
  activeFilters.date_to = filters.date_to || '';
  const employeeHrId = Number(filters.employee_hr_id);
  activeFilters.employee_hr_id = Number.isInteger(employeeHrId) && employeeHrId > 0
    ? employeeHrId
    : null;
  activeFilters.employee_label = filters.employee_label || '';
}

function buildRequestParams(offset = 0) {
  const params = { limit: pageSize, offset };
  if (activeFilters.search) params.search = activeFilters.search;
  if (activeFilters.action) params.action = activeFilters.action;
  if (activeFilters.entity_type) params.entity_type = activeFilters.entity_type;
  if (activeFilters.date_from) params.date_from = activeFilters.date_from;
  if (activeFilters.date_to) params.date_to = activeFilters.date_to;
  if (activeFilters.employee_hr_id) params.employee_hr_id = activeFilters.employee_hr_id;
  return params;
}

async function loadEntries() {
  loading.value = true;
  error.value = null;
  try {
    const data = await fetchGlobalAudit(buildRequestParams(0));
    entries.value = data.entries;
    total.value = data.total;
  } catch (err) {
    error.value = err?.message || 'Failed to load change log.';
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  if (loadingMore.value || entries.value.length >= total.value) return;
  loadingMore.value = true;
  try {
    const data = await fetchGlobalAudit(buildRequestParams(entries.value.length));
    entries.value = entries.value.concat(data.entries);
    total.value = data.total;
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Load failed',
      detail: err?.message || 'Could not load more entries.',
      life: 3500,
    });
  } finally {
    loadingMore.value = false;
  }
}

function applyFilters() {
  if (filters.date_from && filters.date_to && !isValidDateRange(filters.date_from, filters.date_to)) {
    toast.add({
      severity: 'warn',
      summary: 'Invalid date range',
      detail: 'Start date must be on or before end date.',
      life: 3000,
    });
    return;
  }
  syncActiveFilters();
  loadEntries();
}

function clearFilters() {
  filters.search = '';
  filters.action = null;
  filters.entity_type = null;
  filters.date_from = '';
  filters.date_to = '';
  filters.employee_hr_id = null;
  filters.employee_label = '';
  filters.employee_snapshot = null;
  syncActiveFilters();
  loadEntries();
}

function targetEmployeePerson(entry) {
  return changelogTargetEmployeePerson(entry);
}

function filterByEmployee(entry) {
  const person = changelogTargetEmployeePerson(entry);
  if (!person) return;
  filters.employee_hr_id = person.value;
  filters.employee_label = person.label;
  filters.employee_snapshot = { ...person };
  applyFilters();
}

async function exportCsv() {
  exporting.value = true;
  try {
    const rows = [
      ['When', 'Actor', 'Employee', 'Action', 'Source', 'Field', 'Old value', 'New value'],
    ];
    let offset = 0;
    const batchSize = 200;
    let fetched = 0;
    let exportTotal = total.value || entries.value.length;

    while (fetched < exportTotal) {
      const data = await fetchGlobalAudit({
        ...buildRequestParams(offset),
        limit: batchSize,
        offset,
      });
      if (!data.entries.length) break;
      data.entries.forEach((entry) => {
        rows.push([
          entry.created_at,
          entry.actor_name || 'System',
          entry.target_label || '',
          auditActionLabel(entry.action),
          changelogEntityTypeLabel(entry.entity_type),
          auditFieldLabel(entry),
          displayAuditValue(entry.old_value),
          displayAuditValue(entry.new_value),
        ]);
      });
      fetched += data.entries.length;
      exportTotal = data.total;
      offset += data.entries.length;
      if (data.entries.length < batchSize) break;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(rows, `change-log-${stamp}.csv`);
  } catch (err) {
    toast.add({
      severity: 'error',
      summary: 'Export failed',
      detail: err?.message || 'Could not export change log.',
      life: 3500,
    });
  } finally {
    exporting.value = false;
  }
}

onMounted(loadEntries);
</script>

<style scoped>
.settings-changelog-body {
  overflow: hidden;
}

.settings-changelog-toolbar {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 2;
  padding-bottom: 0.25rem;
  background: var(--crc-bar-bg, var(--surface-card));
  border-bottom: 1px solid var(--crc-panel-border, var(--surface-border));
}

.settings-changelog-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
}

.settings-changelog-results {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
}

.settings-changelog-change {
  max-width: 22rem;
}

.settings-changelog-employee-btn {
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
  border-radius: 0.375rem;
}

.settings-changelog-employee-btn:hover :deep(.text-sm) {
  color: var(--crc-green, #1d9e75);
  text-decoration: underline;
}

.settings-changelog-employee-btn:focus-visible {
  outline: 2px solid var(--crc-green, #1d9e75);
  outline-offset: 2px;
}

.settings-changelog-source {
  font-size: 0.75rem;
  color: var(--crc-text-muted, var(--text-color-secondary));
  white-space: nowrap;
}
</style>
